<script setup>
// Astra DS · DayBar — la regla del día.
//
// Figma: Pattern/DayBar 698:508.
//
// «Cada franja pesa lo que dura el programa; la aguja marca la hora actual.
// Escala con el contenedor: no lleva auto-layout, lleva restricciones.»
//
// Esa nota es de Figma, donde repartir en proporción exige quitar el
// auto-layout: `layoutGrow` solo acepta enteros y no sabe decir «esta franja
// pesa 244 y esta 122». En CSS el problema no existe —`flex-grow` acepta
// decimales— así que aquí sí es un flex y cada franja crece según su duración.
// Es una divergencia consciente Figma↔CSS, la misma que ya se anotó en DataRow.
import { computed } from "vue";

const props = defineProps({
  // Franjas del día: { label, start, end, live }. `start` y `end` en minutos
  // desde medianoche. Una franja que cruza la medianoche lleva `end` por
  // encima de 1440 (00:00 del día siguiente = 1440), y así sigue midiendo lo
  // que dura en vez de medir en negativo.
  slots: { type: Array, default: () => [] },

  // Minuto actual del día. Fuera del rango pintado, la aguja no se pinta:
  // una aguja clavada en el borde diría que son las seis cuando son las tres.
  now: { type: Number, default: null },

  // Lo que se lee en la etiqueta de la aguja.
  nowLabel: { type: String, default: "" },
});

const start = computed(() =>
  props.slots.length ? Math.min(...props.slots.map((s) => s.start)) : 0,
);
const end = computed(() =>
  props.slots.length ? Math.max(...props.slots.map((s) => s.end)) : 1440,
);
const span = computed(() => Math.max(end.value - start.value, 1));

// El porcentaje se calcula aquí y no en CSS porque el peso de cada franja es
// un dato de la parrilla, no una decisión de maquetación.
const bands = computed(() =>
  props.slots.map((slot) => ({
    ...slot,
    width: ((slot.end - slot.start) / span.value) * 100,
  })),
);

const needle = computed(() => {
  if (props.now == null) return null;
  if (props.now < start.value || props.now > end.value) return null;
  return ((props.now - start.value) / span.value) * 100;
});
</script>

<template>
  <div class="day-bar" role="presentation">
    <span
      v-for="band in bands"
      :key="band.label + band.start"
      class="day-bar__band"
      :class="{ 'is-live': band.live }"
      :style="{ flexGrow: band.width, flexBasis: 0 }"
    >
      {{ band.label }}
    </span>

    <span
      v-if="needle !== null"
      class="day-bar__needle"
      :style="{ left: `${needle}%` }"
    >
      <span v-if="nowLabel" class="day-bar__now">{{ nowLabel }}</span>
    </span>
  </div>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

.day-bar {
  position: relative;
  display: flex;
  box-sizing: border-box;
  height: 44px;
  border-bottom: 1px solid var(--color-border-default);
  background: var(--color-bg-elevated);
}

.day-bar__band {
  @include t.mono-nano;
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 0 9px;
  border-right: 1px solid var(--color-border-default);
  color: var(--color-text-tertiary);
  white-space: nowrap;
  overflow: hidden;

  &:last-child {
    border-right: 0;
  }

  // El programa en antena es el único tramo en rojo de la barra. Si hubiera
  // dos, ninguno sería el que está sonando.
  &.is-live {
    background: var(--color-bg-accent);
    color: var(--color-text-on-action);
  }
}

.day-bar__needle {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 3;
  width: 1px;
  background: var(--color-text-primary);
}

// Cuelga de la aguja hacia la derecha. Anclada a la izquierda de la línea y no
// centrada: centrada, a las 23:50 se sale de la barra.
.day-bar__now {
  @include t.mono-nano;
  position: absolute;
  top: 0;
  left: 1px;
  padding: 2px 4px;
  background: var(--color-bg-inverse);
  color: var(--color-text-inverse);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.06em;
  line-height: 1;
  white-space: nowrap;
}
</style>
